import { PlayArrow } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { Box, Button, Drawer, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EUnitType } from '../../../@types/Enums';
import { Side } from '../../../@types/Side';
import { useAttributeActions } from '../../../hooks/useAttributeActions';
import { IWall } from '../../../models/abstract/Wall';
import AttributeModel, { IAttribute } from '../../../models/standard/AttributeModel';
import StandardWallModel from '../../../models/standard/StandardWallModel';
import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { usePlan } from '../../../provider/PlanProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import { getDefaultObjectId, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';
import { deleteButton, immersightBlue } from '../../../utils/colors';
import {
  MAX_RADIATOR_DEPTH,
  MAX_RADIATOR_DEPTH_IMPERIAL,
  MAX_RADIATOR_LENGTH,
  MAX_RADIATOR_LENGTH_IMPERIAL,
  MIN_RADIATOR_DEPTH,
  MIN_RADIATOR_DEPTH_IMPERIAL,
  MIN_RADIATOR_LENGTH,
  MIN_RADIATOR_LENGTH_IMPERIAL
} from '../../../utils/constants';
import { cmToInternalRounded, getLocalizedUnitLabel, inchToInternalRounded, internalToCentimetersRounded, internalToInchesRounded } from '../../../utils/conversionUtils';
import filesApiService from '../../../utils/filesApiService';
import { handleWheelInput, WheelInputConfig } from '../../../utils/handleMouseEvents';
import AttributeAutocomplete from '../AttributeAutocomplete';
import AttributeList from '../AttributeList';

export interface RadiatorDrawerProps {
  open: boolean;
  onClose: () => void;
  /**
   * The selected radiator data containing the wall index and radiator index.
   */
  selectedRadiator: {
    wallIndex: number;
    radiatorIndex: number;
    side: Side;
  };

  resetSelection: () => void;
  id: string;
}

const RadiatorDrawer = ({ open, onClose, selectedRadiator, resetSelection, id }: RadiatorDrawerProps): JSX.Element => {
  const { building, updateRadiatorWidth, updateRadiatorThickness, deleteRadiator, updateRadiatorAttributes, updateRadiatorNicheDepth, defaultObjects } = useBuilding();
  const { unitType } = useUnitContext();
  const intl = useIntl();

  const wallIndex = selectedRadiator.wallIndex;
  const radiatorIndex = selectedRadiator.radiatorIndex;
  const side = selectedRadiator.side;
  const wall = building.walls[wallIndex] as IWall;
  const radiator = wall?.sides?.[side]?.radiators?.[radiatorIndex];

  const initialRadiatorWidth = radiator?.length ? internalToCentimetersRounded(radiator.length) : MIN_RADIATOR_LENGTH;
  const initialRadiatorWidthImperial = radiator?.length ? internalToInchesRounded(radiator.length) : MIN_RADIATOR_LENGTH_IMPERIAL;

  const initialRadiatorThickness = radiator?.depth ? internalToCentimetersRounded(radiator.depth) : MIN_RADIATOR_DEPTH;
  const initialRadiatorThicknessImperial = radiator?.depth ? internalToInchesRounded(radiator.depth) : MIN_RADIATOR_DEPTH_IMPERIAL;

  const initialNicheDepth = radiator?.nicheDepth !== undefined ? internalToCentimetersRounded(radiator.nicheDepth) : 0;
  const initialNicheDepthImperial = radiator?.nicheDepth !== undefined ? internalToInchesRounded(radiator.nicheDepth) : 0;

  const [radiatorWidth, setRadiatorWidth] = useState<number>(unitType === EUnitType.METRIC ? initialRadiatorWidth : initialRadiatorWidthImperial);
  const [widthInput, setWidthInput] = useState<number>(radiatorWidth);
  const [radiatorThickness, setRadiatorThickness] = useState<number>(unitType === EUnitType.METRIC ? initialRadiatorThickness : initialRadiatorThicknessImperial);
  const [thicknessInput, setThicknessInput] = useState<number>(radiatorThickness);

  const [nicheDepth, setNicheDepth] = useState<number>(unitType === EUnitType.METRIC ? initialNicheDepth : initialNicheDepthImperial);
  const [nicheInput, setNicheInput] = useState<number>(nicheDepth);

  const [attributes, setAttributes] = useState<AttributeModel[]>(radiator?.attributes ?? []);
  const [attributeValues, setAttributeValues] = useState<Record<number, string>>(
    (radiator?.attributes ?? []).reduce(
      (acc, a) => {
        if (a.value) acc[a.id] = a.value;
        return acc;
      },
      {} as Record<number, string>,
    ),
  );
  const [showInputFields, setShowInputFields] = useState<Record<number, boolean>>(
    (radiator?.attributes ?? []).reduce(
      (acc, a) => {
        acc[a.id] = !!a.value;
        return acc;
      },
      {} as Record<number, boolean>,
    ),
  );
  const [allAttributes, setAllAttributes] = useState<IAttribute[]>([]);
  const [attrSelected, setAttrSelected] = useState<IAttribute | null>(null);
  const [attrInputValue, setAttrInputValue] = useState('');
  const [attrError, setAttrError] = useState<string | null>(null);

  const { handleAttributeValueChange, handleDeleteAttribute, toggleInputField, handleAttributeSelection, getSortedAttributes } = useAttributeActions(
    attributes,
    setAttributes,
    setAttributeValues,
    setShowInputFields,
    setAttrSelected,
    setAttrInputValue,
    (attrs) => updateRadiatorAttributes(wallIndex, side, radiatorIndex, attrs),
    allAttributes,
    setAllAttributes,
    setAttrError,
  );

  const handleAttributeUnitChange = useCallback(
    (attrId: number, unit: string) => {
      const updated = attributes.map((a) =>
        a.id === attrId
          ? new AttributeModel(
              a.id,
              a.key,
              a.value,
              a.predecessorId,
              a.type,
              unit, // Update Unit
              a.hidden,
              a.forced,
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily,
            )
          : a,
      );
      setAttributes(updated);
      updateRadiatorAttributes(wallIndex, side, radiatorIndex, updated);
    },
    [attributes, updateRadiatorAttributes, wallIndex, side, radiatorIndex],
  );

  const cmLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'centimeter'), [intl]);
  const inLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'inch'), [intl]);

  const oneCmInternal = useMemo(() => cmToInternalRounded(1), []);
  const nicheMaxInternal = useMemo(() => {
    const wallDepthInternal = (wall as StandardWallModel)?.depth ?? 0;
    return Math.max(0, wallDepthInternal - oneCmInternal);
  }, [wall, oneCmInternal]);
  const nicheMaxInUnit = useMemo(() => {
    return unitType === EUnitType.METRIC ? internalToCentimetersRounded(nicheMaxInternal) : internalToInchesRounded(nicheMaxInternal);
  }, [unitType, nicheMaxInternal]);
  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  useEffect(() => {
    if (wall && radiator) {
      if (unitType === EUnitType.METRIC) {
        const newWidth = radiator.length ? internalToCentimetersRounded(radiator.length) : MIN_RADIATOR_LENGTH;
        const newThickness = radiator.depth ? internalToCentimetersRounded(radiator.depth) : MIN_RADIATOR_DEPTH;
        const newNiche = radiator.nicheDepth !== undefined ? internalToCentimetersRounded(radiator.nicheDepth) : 0;
        setRadiatorWidth(newWidth);
        setWidthInput(newWidth);
        setRadiatorThickness(newThickness);
        setThicknessInput(newThickness);
        setNicheDepth(newNiche);
        setNicheInput(newNiche);
      } else {
        const newWidth = radiator.length ? internalToInchesRounded(radiator.length) : MIN_RADIATOR_LENGTH_IMPERIAL;
        const newThickness = radiator.depth ? internalToInchesRounded(radiator.depth) : MIN_RADIATOR_DEPTH_IMPERIAL;
        const newNiche = radiator.nicheDepth !== undefined ? internalToInchesRounded(radiator.nicheDepth) : 0;
        setRadiatorWidth(newWidth);
        setWidthInput(newWidth);
        setRadiatorThickness(newThickness);
        setThicknessInput(newThickness);
        setNicheDepth(newNiche);
        setNicheInput(newNiche);
      }
      setAttributes(radiator.attributes ?? []);
      setAttributeValues(
        (radiator.attributes ?? []).reduce(
          (acc, a) => {
            if (a.value) acc[a.id] = a.value;
            return acc;
          },
          {} as Record<number, string>,
        ),
      );
      setShowInputFields(
        (radiator.attributes ?? []).reduce(
          (acc, a) => {
            acc[a.id] = !!a.value;
            return acc;
          },
          {} as Record<number, boolean>,
        ),
      );
    } else {
      onClose();
    }
  }, [radiator, wall, unitType, onClose]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const catalog = await filesApiService.getAttributes(regionCode);
        setAllAttributes(catalog);
        setAttrError(null);
      } catch (e) {
        setAttrError(`Error fetching attributes: ${(e as Error).message}`);
      }
    })();
  }, [open, regionCode]);

  useEffect(() => {
    const currentInternal = radiator?.nicheDepth ?? 0;
    if (currentInternal > nicheMaxInternal) {
      updateRadiatorNicheDepth(wallIndex, side, radiatorIndex, nicheMaxInternal);
    }
  }, [nicheMaxInternal, radiator?.nicheDepth, wallIndex, radiatorIndex, updateRadiatorNicheDepth]);

  const validateAndSetWidth = useCallback(
    (value: number): void => {
      const minValue = unitType === EUnitType.METRIC ? MIN_RADIATOR_LENGTH : MIN_RADIATOR_LENGTH_IMPERIAL;
      const maxValue = unitType === EUnitType.METRIC ? MAX_RADIATOR_LENGTH : MAX_RADIATOR_LENGTH_IMPERIAL;

      if (!isNaN(value)) {
        if (value < minValue) {
          value = minValue;
        } else if (value > maxValue) {
          value = maxValue;
        }
        const roundedInternalValue = unitType === EUnitType.METRIC ? cmToInternalRounded(value) : inchToInternalRounded(value);
        setRadiatorWidth(value);
        setWidthInput(value);
        updateRadiatorWidth(wallIndex, side, radiatorIndex, roundedInternalValue);
      } else {
        const fallback = unitType === EUnitType.METRIC ? initialRadiatorWidth : initialRadiatorWidthImperial;
        setRadiatorWidth(fallback);
        setWidthInput(fallback);
      }
    },
    [unitType, wallIndex, radiatorIndex, initialRadiatorWidth, initialRadiatorWidthImperial, updateRadiatorWidth],
  );

  const validateAndSetThickness = useCallback(
    (value: number): void => {
      const minValue = unitType === EUnitType.METRIC ? MIN_RADIATOR_DEPTH : MIN_RADIATOR_DEPTH_IMPERIAL;
      const maxValue = unitType === EUnitType.METRIC ? MAX_RADIATOR_DEPTH : MAX_RADIATOR_DEPTH_IMPERIAL;

      if (!isNaN(value)) {
        if (value < minValue) {
          value = minValue;
        } else if (value > maxValue) {
          value = maxValue;
        }
        const roundedInternalValue = unitType === EUnitType.METRIC ? cmToInternalRounded(value) : inchToInternalRounded(value);
        setRadiatorThickness(value);
        setThicknessInput(value);
        updateRadiatorThickness(wallIndex, side, radiatorIndex, roundedInternalValue);
      } else {
        const fallback = unitType === EUnitType.METRIC ? initialRadiatorThickness : initialRadiatorThicknessImperial;
        setRadiatorThickness(fallback);
        setThicknessInput(fallback);
      }
    },
    [unitType, wallIndex, radiatorIndex, initialRadiatorThickness, initialRadiatorThicknessImperial, updateRadiatorThickness],
  );

  const validateAndSetNicheDepth = useCallback(
    (value: number): void => {
      if (isNaN(value)) {
        const fallback = unitType === EUnitType.METRIC ? initialNicheDepth : initialNicheDepthImperial;
        setNicheDepth(fallback);
        setNicheInput(fallback);
        return;
      }
      const inputInternal = unitType === EUnitType.METRIC ? cmToInternalRounded(value) : inchToInternalRounded(value);
      const clampedInternal = Math.min(Math.max(0, inputInternal), nicheMaxInternal);
      updateRadiatorNicheDepth(wallIndex, side, radiatorIndex, clampedInternal);
      const displayValue = unitType === EUnitType.METRIC ? internalToCentimetersRounded(clampedInternal) : internalToInchesRounded(clampedInternal);
      setNicheDepth(displayValue);
      setNicheInput(displayValue);
    },
    [unitType, wallIndex, radiatorIndex, updateRadiatorNicheDepth, initialNicheDepth, initialNicheDepthImperial, nicheMaxInternal],
  );

  const handleWidthInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;
    const parsedValue = parseFloat(newValue);
    if (!isNaN(parsedValue)) {
      setWidthInput(parsedValue);
    }
  }, []);

  const handleWidthInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      const value = parseFloat(event.target.value);
      validateAndSetWidth(value);
    },
    [validateAndSetWidth],
  );

  const handleWidthKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter') {
        const value = parseFloat((event.target as HTMLInputElement).value);
        validateAndSetWidth(value);
        event.currentTarget.blur();
      }
    },
    [validateAndSetWidth],
  );

  const handleThicknessInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;
    const parsedValue = parseFloat(newValue);
    if (!isNaN(parsedValue)) {
      setThicknessInput(parsedValue);
    }
  }, []);

  const handleThicknessInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      const value = parseFloat(event.target.value);
      validateAndSetThickness(value);
    },
    [validateAndSetThickness],
  );

  const handleThicknessKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter') {
        const value = parseFloat((event.target as HTMLInputElement).value);
        validateAndSetThickness(value);
        event.currentTarget.blur();
      }
    },
    [validateAndSetThickness],
  );

  const handleNicheInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;
    const parsedValue = parseFloat(newValue);
    if (!isNaN(parsedValue)) {
      setNicheInput(parsedValue);
    }
  }, []);

  const handleNicheInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      const value = parseFloat(event.target.value);
      validateAndSetNicheDepth(value);
    },
    [validateAndSetNicheDepth],
  );

  const handleNicheKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter') {
        const value = parseFloat((event.target as HTMLInputElement).value);
        validateAndSetNicheDepth(value);
        event.currentTarget.blur();
      }
    },
    [validateAndSetNicheDepth],
  );

  const handleCloseDrawer = useCallback(() => {
    resetSelection();
    onClose();
  }, [resetSelection, onClose]);

  const handleDeleteRadiator = useCallback(() => {
    deleteRadiator(wallIndex, side, radiatorIndex);
    resetSelection();
    onClose();
  }, [deleteRadiator, wallIndex, radiatorIndex, resetSelection, onClose]);

  const widthWheelConfig: WheelInputConfig = {
    unitType,
    minMetric: MIN_RADIATOR_LENGTH,
    maxMetric: MAX_RADIATOR_LENGTH,
    minInches: MIN_RADIATOR_LENGTH_IMPERIAL,
    maxInches: MAX_RADIATOR_LENGTH_IMPERIAL,
  };

  const thicknessWheelConfig: WheelInputConfig = {
    unitType,
    minMetric: MIN_RADIATOR_DEPTH,
    maxMetric: MAX_RADIATOR_DEPTH,
    minInches: MIN_RADIATOR_DEPTH_IMPERIAL,
    maxInches: MAX_RADIATOR_DEPTH_IMPERIAL,
  };

  const nicheWheelConfig: WheelInputConfig = {
    unitType,
    minMetric: 0,
    maxMetric: nicheMaxInUnit,
    minInches: 0,
    maxInches: nicheMaxInUnit,
  };

  const localizedRadiatorName = useMemo(() => {
    const objId = getDefaultObjectId(ObjectType.Radiator);
    const apiObj = defaultObjects?.find((o: any) => o.id === objId);
    const { locale } = intl;

    if (apiObj && apiObj.localization) {
      const currentLocale = locale.split('-')[0];
      return apiObj.localization[currentLocale] || apiObj.localization.en || apiObj.name;
    }

    return intl.formatMessage({ id: 'RadiatorDrawer.header', defaultMessage: 'Heizkörper' });
  }, [defaultObjects, intl]);
  return (
    <Drawer
      key={id}
      anchor="right"
      open={open}
      variant={open ? 'permanent' : 'temporary'}
      sx={{
        '& .MuiPaper-root': {
          top: '64px',
          zIndex: 1000,
          width: '420px',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      <Grid container direction="column" p={0}>
        <Grid
          item
          sx={{
            backgroundColor: immersightBlue,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
          }}
        >
          <Box sx={{ marginLeft: '16px' }}>
            <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
              {localizedRadiatorName}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDrawer}
            sx={{
              color: '#fff',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <PlayArrow sx={{ fontSize: 30 }} />
          </IconButton>
        </Grid>
      </Grid>
      <Grid container direction="column" spacing={3} p={3}>
        {/* Radiator Width */}
        <Grid item>
          <Typography variant="subtitle2" gutterBottom sx={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
            <FormattedMessage
              id="RadiatorDrawer.radiatorWidth"
              defaultMessage="Radiator Width ({unit})"
              values={{ unit: unitType === EUnitType.METRIC ? cmLocalizedLabel : inLocalizedLabel }}
            />
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={widthInput}
            onChange={handleWidthInputChange}
            onBlur={handleWidthInputBlur}
            onKeyUp={handleWidthKeyPress}
            onWheel={(e) =>
              handleWheelInput(
                e,
                widthInput.toString(),
                (v: string) => {
                  const newVal = parseFloat(v);
                  if (!isNaN(newVal)) {
                    setWidthInput(newVal);
                  }
                },
                widthWheelConfig,
              )
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SyncAltIcon />
                </InputAdornment>
              ),
              inputProps: {
                min: unitType === EUnitType.METRIC ? MIN_RADIATOR_LENGTH : MIN_RADIATOR_LENGTH_IMPERIAL,
                max: unitType === EUnitType.METRIC ? MAX_RADIATOR_LENGTH : MAX_RADIATOR_LENGTH_IMPERIAL,
              },
            }}
          />
        </Grid>

        {/* Radiator Thickness */}
        <Grid item>
          <Typography variant="subtitle2" gutterBottom sx={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
            <FormattedMessage
              id="RadiatorDrawer.radiatorThickness"
              defaultMessage="Radiator Thickness ({unit})"
              values={{ unit: unitType === EUnitType.METRIC ? cmLocalizedLabel : inLocalizedLabel }}
            />
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={thicknessInput}
            onChange={handleThicknessInputChange}
            onBlur={handleThicknessInputBlur}
            onKeyUp={handleThicknessKeyPress}
            onWheel={(e) =>
              handleWheelInput(
                e,
                thicknessInput.toString(),
                (v: string) => {
                  const newVal = parseFloat(v);
                  if (!isNaN(newVal)) {
                    setThicknessInput(newVal);
                  }
                },
                thicknessWheelConfig,
              )
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SyncAltIcon />
                </InputAdornment>
              ),
              inputProps: {
                min: unitType === EUnitType.METRIC ? MIN_RADIATOR_DEPTH : MIN_RADIATOR_DEPTH_IMPERIAL,
                max: unitType === EUnitType.METRIC ? MAX_RADIATOR_DEPTH : MAX_RADIATOR_DEPTH_IMPERIAL,
              },
            }}
          />
        </Grid>

        {/* Radiator Niche Depth */}
        <Grid item>
          <Typography variant="subtitle2" gutterBottom sx={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
            <FormattedMessage
              id="RadiatorDrawer.nicheDepth"
              defaultMessage="Niche Depth ({unit})"
              values={{ unit: unitType === EUnitType.METRIC ? cmLocalizedLabel : inLocalizedLabel }}
            />
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={nicheInput}
            onChange={handleNicheInputChange}
            onBlur={handleNicheInputBlur}
            onKeyUp={handleNicheKeyPress}
            onWheel={(e) =>
              handleWheelInput(
                e,
                nicheInput.toString(),
                (v: string) => {
                  const newVal = parseFloat(v);
                  if (!isNaN(newVal)) {
                    setNicheInput(newVal);
                  }
                },
                nicheWheelConfig,
              )
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SyncAltIcon />
                </InputAdornment>
              ),
              inputProps: {
                min: 0,
                max: nicheMaxInUnit,
                step: unitType === EUnitType.METRIC ? 0.1 : 0.01,
              },
            }}
          />
        </Grid>

        {/* Attribute List */}
        <Grid item>
          <AttributeList
            attributes={getSortedAttributes(attributes)}
            attributeValues={attributeValues}
            showInputFields={showInputFields}
            onAttributeValueChange={handleAttributeValueChange}
            onAttributeUnitChange={handleAttributeUnitChange} // Wired Up!
            onDeleteAttribute={handleDeleteAttribute}
            onToggleInputField={toggleInputField}
            onReorderAttributes={(newAttrs) => {
              setAttributes(newAttrs);
              updateRadiatorAttributes(wallIndex, side, radiatorIndex, newAttrs);
            }}
          />
        </Grid>
        {/* Attribute Autocomplete */}
        <Grid item>
          <Box
            sx={{
              mt: -5,
              mb: 1,
              width: '99%',
              mx: 'auto',
            }}
          >
            <AttributeAutocomplete
              attributes={allAttributes}
              disabledAttributeIds={attributes.map((attribute) => attribute.id)}
              selected={attrSelected}
              inputValue={attrInputValue}
              error={attrError}
              onChange={handleAttributeSelection}
              onInputChange={(val) => {
                setAttrInputValue(val);
                if (val !== attrSelected?.key) setAttrSelected(null);
              }}
              placeholder={intl.formatMessage({ id: 'RadiatorDrawer.addAttribute', defaultMessage: 'Add attribute' })}
            />
          </Box>
        </Grid>

        {/* Delete Radiator Button */}
        <Grid item>
          <Button variant="contained" startIcon={<DeleteIcon />} onClick={handleDeleteRadiator} sx={{ backgroundColor: deleteButton }}>
            <FormattedMessage id="RadiatorDrawer.deleteRadiator" defaultMessage="Delete Radiator" />
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default RadiatorDrawer;
